import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tool, ToolFormData, ToolCategory, CONNECTOR_AFFILIATE_LINKS } from '@/types/database';
import { useToast } from '@/hooks/use-toast';
export function useTools() {
  return useQuery({
    queryKey: ['tools'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tools')
        .select('*')
        .order('name');

      if (error) throw error;
      return data as Tool[];
    },
  });
}

export function useActiveTools() {
  return useQuery({
    queryKey: ['tools', 'active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tools')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data as Tool[];
    },
  });
}

export function useTool(slug: string) {
  return useQuery({
    queryKey: ['tools', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tools')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;
      return data as Tool | null;
    },
    enabled: !!slug,
  });
}

export function useToolById(id: string) {
  return useQuery({
    queryKey: ['tools', 'id', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tools')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      return data as Tool | null;
    },
    enabled: !!id,
  });
}

export function useCreateTool() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (tool: ToolFormData) => {
      const { data, error } = await supabase
        .from('tools')
        .insert({
          ...tool,
          category: tool.category as ToolCategory,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      toast({
        title: 'Tool created',
        description: 'The tool has been created successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error creating tool',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateTool() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...tool }: ToolFormData & { id: string }) => {
      const { data, error } = await supabase
        .from('tools')
        .update({
          ...tool,
          category: tool.category as ToolCategory,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      toast({
        title: 'Tool updated',
        description: 'The tool has been updated successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error updating tool',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteTool() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('tools').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      toast({
        title: 'Tool deleted',
        description: 'The tool has been deleted successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error deleting tool',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Fetch automation platform tools (Make, Zapier, n8n) for dynamic affiliate links
export function useAutomationPlatformTools() {
  return useQuery({
    queryKey: ['tools', 'automation-platforms'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tools')
        .select('*')
        .in('slug', ['make', 'zapier', 'n8n', 'lovable'])
        .eq('is_active', true);

      if (error) throw error;
      
      // Create a map by slug for easy access
      const toolMap: Record<string, Tool> = {};
      data?.forEach((tool) => {
        toolMap[tool.slug] = tool as Tool;
      });
      
      return toolMap;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

// Get affiliate link for a connector type from tools database
export function getConnectorAffiliateLink(
  platformTools: Record<string, Tool> | undefined,
  connector: 'make' | 'zapier' | 'n8n' | 'both'
): { url: string; label: string } {
  // For 'both', prefer Make
  const slug = connector === 'both' ? 'make' : connector;
  
  // Try to get from database first
  if (platformTools) {
    const tool = platformTools[slug];
    if (tool?.affiliate_link) {
      return {
        url: tool.affiliate_link,
        label: `Try ${tool.name} Free`,
      };
    }
  }

  // Fallback to hardcoded affiliate links with tracking params
  return CONNECTOR_AFFILIATE_LINKS[connector];
}
